import { stripeService } from '@/services/stripe';
import type { ICheckoutContext } from '@/types/context';
import { PaymentMethodEnum, ProviderEnum } from '@/types/common';
import { buildStripeIdealPayRequest } from '@/utils/ideal';
import type { StripePaymentResponse } from '@/types/payment';
import type { IdealPaymentParams } from '@/types/paymentHandlers';

// Stripe-specific iDEAL handler
const handleStripeIdeal = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse>(params: unknown) => Promise<TResponse>,
  params: IdealPaymentParams,
  idealSettings: NonNullable<ReturnType<ICheckoutContext['paymentMethodsSettings']['find']>>
): Promise<StripePaymentResponse> => {
  const { elements } = params;

  const billingDetails = {
    email: checkout.customer.email,
    address: {
      country: checkout.customer.geoLocator.countryCode2,
    },
  };

  const paymentMethod = await stripeService.createIdealPaymentMethod(elements, billingDetails);

  const request = buildStripeIdealPayRequest(paymentMethod, idealSettings, checkout.customer.email || '');

  const response = await sendPaymentToServer<StripePaymentResponse>(request);
  return response;
};

export const handleIdealPayment = async (
  checkout: ICheckoutContext,
  sendPaymentToServer: <TResponse>(params: unknown) => Promise<TResponse>,
  params?: IdealPaymentParams
): Promise<StripePaymentResponse> => {
  if (!params) {
    throw new Error('iDEAL payment requires elements parameter');
  }

  const idealSettings = checkout.paymentMethodsSettings.find((pm) => pm.paymentMethodName === PaymentMethodEnum.IDEAL);

  if (!idealSettings) {
    throw new Error('iDEAL settings not found');
  }

  switch (idealSettings.provider) {
    case ProviderEnum.STRIPE:
      return await handleStripeIdeal(checkout, sendPaymentToServer, params, idealSettings);
    default:
      throw new Error(`Unsupported provider for iDEAL: ${idealSettings.provider}`);
  }
};
